/**
 * Shared atoms for the Create-Project Wizard.
 * Sprint P37r · Feb 2026.
 *
 * These atoms are visually identical to the Command Center's atoms
 * but reside in their own module so the Command Center's atoms don't
 * become a bloated shared surface. When we need a truly cross-page
 * atom, we'll move it to `frontend/src/components/atoms`.
 */
import { CheckCircle, WarningCircle, XCircle, Sparkle } from "@phosphor-icons/react";

export function CardShell({ number, title, subtitle, testId, icon: Icon, actions, id, children }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.03)] scroll-mt-32"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        {number != null && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-blue/10 text-brand-blue font-mono text-[11px] font-bold">
            {number}
          </span>
        )}
        {Icon && <Icon size={14} className="text-brand-blue" weight="duotone" />}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-[13.5px] tracking-tight text-slate-900 truncate">{title}</h3>
          {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
        </div>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Field({ label, value, onChange, placeholder, type = "text", suffix, hint, disabled, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
        {hint && <span className="text-[9.5px] text-slate-400">{hint}</span>}
      </div>
      <div className={`relative flex items-center rounded-md border transition-all ${disabled
        ? "border-slate-100 bg-slate-50"
        : "border-slate-200 bg-white focus-within:border-brand-blue focus-within:ring-1 focus-within:ring-brand-blue/30"}`}>
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange?.(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          className="flex-1 bg-transparent px-2.5 py-1.5 text-[12.5px] text-slate-800 focus:outline-none disabled:text-slate-500"
        />
        {suffix && <span className="pr-2.5 text-[11px] text-slate-400 font-mono">{suffix}</span>}
      </div>
    </label>
  );
}

export function ReadField({ label, value, mono, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</div>
      <div className={`text-[12.5px] text-slate-800 ${mono ? "font-mono" : ""}`} data-testid={testId}>
        {value == null || value === "" ? <span className="text-slate-400">—</span> : String(value)}
      </div>
    </div>
  );
}

export function ConfidenceBadge({ value }) {
  const pct = typeof value === "number" ? Math.round(value <= 1 ? value * 100 : value) : 0;
  const tone = pct >= 85 ? "emerald" : pct >= 60 ? "amber" : "rose";
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[9.5px] font-mono uppercase tracking-wide ${cls}`}>
      <Sparkle size={9} /> {pct}%
    </span>
  );
}

export function StatusPill({ status, label, size = "sm" }) {
  const map = {
    pass:     { tone: "emerald", Icon: CheckCircle,   label: label || "Pass" },
    complete: { tone: "emerald", Icon: CheckCircle,   label: label || "Complete" },
    warning:  { tone: "amber",   Icon: WarningCircle, label: label || "Warning" },
    missing:  { tone: "rose",    Icon: XCircle,       label: label || "Missing" },
  };
  const meta = map[status] || map.missing;
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
  }[meta.tone];
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-widest ${cls} ${size === "xs" ? "text-[9px]" : ""}`}>
      <Icon size={size === "xs" ? 9 : 10} weight="fill" /> {meta.label}
    </span>
  );
}

export function SectionNav({ active, onJump, items }) {
  return (
    <aside className="w-56 shrink-0 sticky top-28 h-[calc(100vh-140px)] overflow-y-auto pr-1"
              data-testid="create-project-sidenav">
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 px-2 pb-2">
        Sections
      </div>
      <nav className="space-y-0.5">
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => onJump(it.id)}
            data-testid={`nav-${it.id}`}
            className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-md text-[12px] transition-colors
                ${active === it.id ? "bg-brand-blue/10 text-slate-900 font-medium" : "hover:bg-slate-50 text-slate-700"}`}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200/70 text-slate-700 font-mono text-[10px]">
              {it.num}
            </span>
            <span className="flex-1 truncate">{it.label}</span>
          </button>
        ))}
      </nav>
    </aside>
  );
}
